import { Card, CardContent } from "@/components/ui/card";
import { Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { resourceCardConfig } from "@/lib/resourceCardConfig";
import type { SchemaNames } from "instantdb/types";
import { useTranslation } from "@/hooks/useTranslation";
import { Heading } from "@/components/ui/heading";

interface NewResourceCardProps {
  type?: SchemaNames;
  title?: string;
  description?: string;
  className?: string;
  onClick: () => void;
}

export function NewResourceCard({
  type,
  title = "",
  description = "",
  className,
  onClick,
}: NewResourceCardProps) {
  const { t } = useTranslation();
  const config = type ? resourceCardConfig[type] : undefined;
  const cardTitle = type ? t(config?.titleKey || "") : title;
  const cardDescription = type ? t(config?.descriptionKey || "") : description;

  return (
    <Card
      className={cn(
        "h-full col-span-12 @[640px]:col-span-6 @[1024px]:col-span-4",
        "relative group transition-all duration-200",
        "border-2 border-dashed border-muted-foreground/30",
        "hover:border-primary/50 hover:bg-accent cursor-pointer min-h-[200px]",
        "active:scale-[0.98] active:opacity-90 hover:shadow-lg hover:shadow-primary/10",
        "hover:scale-[1.02] transform-gpu select-none",
        className,
      )}
      onClick={onClick}
    >
      <CardContent className="h-full flex flex-col items-center justify-center p-6 text-center">
        <div className="space-y-4">
          <div className="p-4 bg-primary/10 rounded-full inline-block group-hover:bg-primary/20 transition-colors duration-200 group-hover:scale-110 transform">
            <Plus className="size-8 text-primary group-hover:text-primary font-bold" />
          </div>
          <div className="space-y-2">
            <Heading
              level="h3"
              size="h6"
              className="font-semibold group-hover:text-primary transition-colors duration-200"
            >
              {cardTitle}
            </Heading>
            <p className="text-sm text-muted-foreground leading-6">{cardDescription}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
